import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { inscriptionStatus, InscriptionStatus } from '../../../services/central-api/third-party-approved-companies';
import { Modal, View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import FormActions from '../../../components/FormActions';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../../services/window-information';
import { translate } from '../../../services/translate';
import { useForm } from 'react-hook-form';
import FormDropDownPicker from '../../../components/FormDropDownPicker';
import { getInscriptionStatusLabel } from '../InscriptionsCommons';
import FormInput from '../../../components/FormInput';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    currentStatus: InscriptionStatus;
    onConfirm: (values: { status: InscriptionStatus; observationText: string; }) => void;
}

export default function InscriptionChangeStatusModal({ isModalVisible, onConfirm, currentStatus, setModalVisible }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    function closeModal() {
        setModalVisible(false);
        form.reset({ status: 'pending', observationText: '' });
    }

    const form = useForm<{ status: InscriptionStatus; observationText: string; }>({
        defaultValues: { status: currentStatus, observationText: '' },
        mode: 'onChange'
    });
    const selectedStatus = form.watch('status');

    useEffect(() => {
        if (isModalVisible) {
            form.reset({ status: currentStatus, observationText: '' });
        }
    }, [currentStatus, isModalVisible]);

    useEffect(() => {
        form.setValue('observationText', '');
    }, [selectedStatus]);

    function formSubmit() {
        onConfirm(form.getValues());
        closeModal();
    }

    const statusOptions = useMemo(() => {
        return Object.values(inscriptionStatus).filter(status => {
            if (['waiting', 'published', 'pending'].includes(status)) {
                return false;
            }
            if (currentStatus === 'published' && status === 'irregular') {
                return false;
            }
            return true;
        }).map(status => {
            return {
                label: translate(getInscriptionStatusLabel(status)),
                value: status,
            };
        });
    }, [currentStatus]);

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('statusChange')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('selectInscriptionNewStatusMessage')}</MyAppText>

                    </View>

                    <FormDropDownPicker
                        label={translate('inscriptionStatus')}
                        items={statusOptions}
                        zIndex={1}
                        fieldName={'status'}
                        form={form}
                        labelStyle={styles.fieldsLabelStyle}
                        viewStyle={{ width: '100%', gap: 8 }}
                        required
                        showTooltip
                        tooltipText={translate('inscriptionStatusHintText')}
                    />

                    {['failed', 'irregular'].includes(selectedStatus) && selectedStatus != currentStatus && <FormInput
                        form={form}
                        fieldName='observationText'
                        viewStyle={{ width: '100%', gap: 8 }}
                        label={translate('justification')}
                        multiline={true}
                        numberOfLines={4}
                        hintText={translate('inscriptionStatusJustificationHintText')}
                        placeholder={translate('inscriptionObservationForStatusPlaceholder')}
                    />}

                    <FormActions
                        viewStyle={{ width: '100%' }}
                        cancelText='goBack'
                        confirmText='saveChanges'
                        onSubmit={formSubmit}
                        onClose={closeModal}
                        disabled={!form.formState.isValid || selectedStatus === currentStatus}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 516,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32,
        alignItems: 'center'
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
}));
